import { Box, Hidden, IconButton, List, ListItem, ListItemIcon, ListItemText, useMediaQuery } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useTheme } from "@mui/material/styles";
import makeStyles from '@mui/styles/makeStyles';
import Typography from "@mui/material/Typography";
import { CheckRounded } from "@mui/icons-material";
import clsx from "clsx";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "../../components/Link";
import GoogleButton from "../../components/oidc/GoogleButton";
import {
  LOCAL_STORAGE_AUTH_ATTEMPTS,
  LOCAL_STORAGE_AUTH_REAUTH,
  LOCAL_STORAGE_AUTH_REDIRECT,
  LOCAL_STORAGE_AUTH_REF,
  LOCAL_STORAGE_AUTH_USER,
  LOCAL_STORAGE_AUTH_UTM,
} from "../../consts/storageKeys";
import { useUserContext } from "../../context/UserContext";
import { useOurRouter } from "../../hooks/useOurRouter";
import LogoSvg from "../../img/logo-on-black.svg";
import LogoBlurpleSvg from "../../img/logo-on-blurple.svg";
import { reclaim } from "../../reclaim-api/index";
import { QueryState } from "../../types/query";
import {
  getLocalStorage,
  getSessionStorage,
  removeLocalStorage,
  removeSessionStorage,
} from "../../utils/local-storage";
import { browser } from "../../utils/platform";
import { getUserOnboardingPath } from "../../utils/router";
import LandingLayout from "../LandingLayout";

const useStyles = makeStyles(
  (theme) => ({
    layout: {
      backgroundColor: theme.colors.logo.darkness,
      color: theme.colors.white,
      [theme.breakpoints.up("md")]: {
        alignItems: "center",
        display: "flex",
        flexDirection: "row-reverse",
        height: "100%",
        justifyContent: "flex-end",
        padding: theme.spacing(0, 3),
      },
    },
    gridContainerz: {
      minHeight: "50vh",
      padding: theme.spacing(8, 2),
      textAlign: "center",
      width: "100vw",
      [theme.breakpoints.up("md")]: {
        minHeight: 0,
        width: "auto",
      },
    },
    form: {
      backgroundColor: theme.palette.primary.main,
      [theme.breakpoints.up("md")]: {
        boxShadow: "0 32px 32px -16px black",
        padding: theme.spacing(7, 4),
        textAlign: "center",
      },
      [theme.breakpoints.up("lg")]: {
        justifyContent: "flex-start",
      },
      "& $wrapper": {
        maxWidth: 400,
      },
    },
    text: {
      padding: theme.spacing(8, 4),
      [theme.breakpoints.up("md")]: {
        maxWidth: "50vw",
        padding: 0,
        textAlign: "left",
        width: "50vw",
      },
      [theme.breakpoints.up("lg")]: {
        justifyContent: "flex-end",
        paddingRight: theme.spacing(10),
      },
      "& $wrapper": {
        maxWidth: 480,
      },
    },
    wrapper: {
      width: "100%",
    },
    logoBtn: {
      display: "inline-block",
      margin: theme.spacing(0, "auto", 2),
      padding: 0,
      [theme.breakpoints.up("md")]: {
        margin: theme.spacing(0, 0, 2),
      },
      "&:hover": {
        background: "transparent",
      },
      "& svg": {
        height: 24,
        width: 120,
      },
    },
    googleBtn: {
      margin: "0 auto",
      maxWidth: 360,
      transform: "scale(1)",
      transition: "transform .2s ease",
      width: "100%",
      "&:hover": {
        transform: "scale(1.02)",
      },
    },
    body: {
      margin: theme.spacing(4, 0),
    },
    signupText: {
      marginTop: theme.spacing(4),
      "& a": {
        color: "inherit",
        textDecoration: "underline",
      },
    },
    o365Callout: {
      backgroundColor: "rgba(0, 0, 0, .08)",
      borderRadius: 8,
      margin: theme.spacing(5, "auto", 0),
      padding: theme.spacing(2),
      [theme.breakpoints.up("md")]: {
        margin: theme.spacing(5, 0, 0),
      },
      "& a": {
        color: "inherit",
        textDecoration: "underline",
      },
    },
    heading: {
      fontWeight: theme.typography.fontWeightBold,
    },
    benefits: {
      ...theme.typography.body2,
      margin: theme.spacing(0, "auto", 2),
      maxWidth: "max-content",
      [theme.breakpoints.up("md")]: {
        ...theme.typography.body1,
        margin: theme.spacing(0, 0, 3),
      },
      "& .MuiListItemText-root": {
        margin: 0,
      },
    },
    checkIcon: {
      color: theme.colors.logo.corn,
      minWidth: 32,
    },
    boldText: {
      fontWeight: theme.typography.fontWeightBold,
    },
    companyLogos: {
      alignItems: "center",
      display: "flex",
      flexWrap: "wrap",
      justifyContent: "center",
      margin: "0 auto",
      maxWidth: 400,
      opacity: 0.8,
      [theme.breakpoints.up("md")]: {
        justifyContent: "flex-start",
        margin: 0,
      },
      "& *": {
        height: 27,
        margin: theme.spacing(1.5, 2, 0, 0),
      },
    },
    indeedLogo: {
      padding: theme.spacing(0.5, 0),
    },
    logoPadding: {
      padding: theme.spacing(0.25, 0),
    },
    lighten: {
      opacity: 0.8,
    },
  }),
  { index: 2, name: "SignupLayout" }
);

export type SignupLayoutProps = {
  title: string;
  displayLoginButton: boolean;
  officeNotice?: boolean;
};

export const SignupLayout: React.FC<SignupLayoutProps> = ({ title, displayLoginButton, officeNotice, children }) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const medium = useMediaQuery(theme.breakpoints.down('lg'));

  const router = useOurRouter<{ reason: string; state: QueryState }>();

  const [{ user, isAuthenticated, status }] = useUserContext();

  const [error, setError] = useState<string | undefined>();

  const urlParams = useMemo(() => new URLSearchParams(browser().isBrowser ? window.location.search : ""), []);

  useEffect(() => {
    if (!router.query?.reason) return;
    const reason = router.query.reason;

    setError(
      "USER_DISABLED" === reason
        ? "Calendar for this email address is linked to another account. Please sign in using your other Google login."
        : "User not authorized."
    );
  }, [router.query?.reason]);

  // Redirect if user is already authenticated
  useEffect(() => {
    if (!router.isReady || !isAuthenticated) return;

    removeLocalStorage(LOCAL_STORAGE_AUTH_REAUTH);
    removeLocalStorage(LOCAL_STORAGE_AUTH_ATTEMPTS);

    const slackTeamId = urlParams.get("slackTeamId");
    const slackUserId = urlParams.get("slackUserId");

    // the check for "://" prevents arbitrary redirection (Open Redirect)
    const oauthRedirect = router.query.state?.redirect;

    const redirect =
      !!oauthRedirect && !oauthRedirect.includes("://")
        ? oauthRedirect
        : getSessionStorage(LOCAL_STORAGE_AUTH_REDIRECT);

    if (redirect) {
      removeSessionStorage(LOCAL_STORAGE_AUTH_REDIRECT);
    }

    // connect slack user
    if (slackUserId && slackTeamId) {
      reclaim.slack.link(slackUserId, slackTeamId).catch((reason) => {
        if (404 === reason.status) {
          console.log("Slack user not found, redirecting", reason);
          reclaim.slack.authRedirect(slackTeamId);
          return;
        } else {
          console.error("Failed to link slack user", reason);
        }
      });
    }

    let pathname = getUserOnboardingPath("welcome");

    if (user?.onboarded) {
      if (redirect) pathname = redirect;
      else if (medium) pathname = "/stats";
      else pathname = "planner";
    }

    void router.push({
      pathname,
      query: router.query,
    });
  }, [isAuthenticated, medium, router, urlParams, user?.onboarded]);

  const handleClick = useCallback(
    (provider: string) => {
      removeLocalStorage(LOCAL_STORAGE_AUTH_REAUTH);
      removeLocalStorage(LOCAL_STORAGE_AUTH_ATTEMPTS);

      const storedUser = getLocalStorage(LOCAL_STORAGE_AUTH_USER);
      const ref = getLocalStorage(LOCAL_STORAGE_AUTH_REF);
      const utm = getLocalStorage(LOCAL_STORAGE_AUTH_UTM);

      const slackTeamId = urlParams.get("slackTeamId");
      const slackUserId = urlParams.get("slackUserId");

      const invite = urlParams.get("invite");

      // the check for "://" prevents arbitrary redirection (Open Redirect)
      const oauthRedirect = router.query.state?.redirect;
      const redirect = `${
        !!oauthRedirect && !oauthRedirect.includes("://")
          ? oauthRedirect
          : getSessionStorage(LOCAL_STORAGE_AUTH_REDIRECT, "/")
      }${!!invite ? "?invite=1" : ""}`;

      removeSessionStorage(LOCAL_STORAGE_AUTH_REDIRECT);

      const state = {
        flow: "default",
        redirect,
        slackTeamId,
        slackUserId,
        ref,
        utm,
      };

      reclaim.users.authRedirect(provider, storedUser?.email, state);
    },
    [router.query.state?.redirect, urlParams]
  );

  return (
    <LandingLayout title={title} loading={["init", "loading"].includes(status) || !!isAuthenticated} hideNav>
      <Box className={classes.layout}>
        <Grid
          container
          className={clsx(classes.gridContainerz, classes.form)}
          justifyContent="center"
          alignItems="center"
        >
          <Box className={classes.wrapper}>
            <Hidden mdUp>
              <IconButton
                className={classes.logoBtn}
                component={Link}
                href={!!user ? "/" : "//reclaim.ai"}
                size="medium"
                aria-label="reclaim.ai"
                disableTouchRipple
                disableRipple
                disableFocusRipple
              >
                <LogoBlurpleSvg />
              </IconButton>
            </Hidden>
            {children}
            {!!displayLoginButton && (
              <GoogleButton
                label="Continue with Google"
                onClick={() => handleClick("google")}
                className={classes.googleBtn}
              />
            )}
            {!!error && (
              <Typography variant="body1" color="error" className={classes.body}>
                {error}&nbsp; If you believe this to be an error, please contact support.
              </Typography>
            )}
            {!!officeNotice && (
              <Typography variant="body2" className={classes.o365Callout}>
                At this time, Reclaim only works on Google Calendar. Interested in Office 365?{" "}
                <Link href="mailto:hello@reclaim.ai?subject=Re:%20Office%20365%20Support%20for%20Reclaim&body=Hi%20Reclaim%20Team!%0D%0A%0D%0AI'm%20an%20Office%20365%20user%20and%20would%20love%20to%20stay%20updated%20when%20Reclaim%20works%20for%20O365%20calendars.%0D%0A%0D%0AThanks!">
                  Let&nbsp;us&nbsp;know
                </Link>
              </Typography>
            )}
            {!!displayLoginButton && (
              <>
                {router.pathname.startsWith("/login") ? (
                  <Typography className={classes.signupText} variant="body2">
                    Don't have an account? <Link href="/signup">Sign up</Link>
                  </Typography>
                ) : (
                  <Typography className={classes.signupText} variant="body2">
                    Have an account? <Link href="/login">Log in</Link>
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Grid>
        <Grid
          container
          className={clsx(classes.gridContainerz, classes.text)}
          justifyContent="center"
          alignItems="center"
        >
          <Box className={classes.wrapper}>
            <Hidden mdDown>
              <IconButton
                className={classes.logoBtn}
                component={Link}
                href={!!user ? "/" : "//reclaim.ai"}
                size="medium"
                aria-label="reclaim.ai"
                disableTouchRipple
                disableRipple
                disableFocusRipple
              >
                <LogoSvg />
              </IconButton>
            </Hidden>
            <Typography className={classes.heading} variant="h1">
              Optimize your calendar, plan a better&nbsp;workweek.
            </Typography>
            <Typography className={classes.lighten}>No credit card required — create a free&nbsp;account.</Typography>
            <List dense className={classes.benefits}>
              <ListItem disableGutters>
                <ListItemIcon className={classes.checkIcon}>
                  <CheckRounded fontSize="small" />
                </ListItemIcon>
                <ListItemText disableTypography>Save up to 40% of your workweek</ListItemText>
              </ListItem>
              <ListItem disableGutters>
                <ListItemIcon className={classes.checkIcon}>
                  <CheckRounded fontSize="small" />
                </ListItemIcon>
                <ListItemText disableTypography>Reduce context switching</ListItemText>
              </ListItem>
              <ListItem disableGutters>
                <ListItemIcon className={classes.checkIcon}>
                  <CheckRounded fontSize="small" />
                </ListItemIcon>
                <ListItemText disableTypography>Improve your weekly planning</ListItemText>
              </ListItem>
              <ListItem disableGutters>
                <ListItemIcon className={classes.checkIcon}>
                  <CheckRounded fontSize="small" />
                </ListItemIcon>
                <ListItemText disableTypography>Prevent unnecessary meetings</ListItemText>
              </ListItem>
            </List>
            <Typography className={classes.boldText}>Trusted by over 16,000 companies:</Typography>
            <Box className={classes.companyLogos}>
              <img src="/img/shopify.svg" alt="shopify" />
              <img className={classes.logoPadding} src="/img/grafana.svg" alt="grafana" />
              <img className={classes.indeedLogo} src="/img/indeed.svg" alt="indeed" />
            </Box>
          </Box>
        </Grid>
      </Box>
    </LandingLayout>
  );
};
